import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  limit,
  orderBy,
  startAfter,
  getCountFromServer,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyDVoLpkAPXyztCDF-_xF1NZwt4oCzMGzK0",
  authDomain: "physiotattva247-prod.firebaseapp.com",
  projectId: "physiotattva247-prod",
  storageBucket: "physiotattva247-prod.appspot.com",
  messagingSenderId: "379628681821",
  appId: "1:379628681821:web:37642c24e9ac2220f76bb4",
  measurementId: "G-0YBFY5KRN3",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

/**
 * Convert date to Firestore Timestamp
 */
const toFirestoreTimestamp = (date) => {
  if (!date) return null;
  if (date instanceof Timestamp) return date;
  if (date instanceof Date) return Timestamp.fromDate(date);
  if (typeof date === "string") return Timestamp.fromDate(new Date(date));
  if (typeof date === "object" && "seconds" in date) {
    return new Timestamp(date.seconds, date.nanoseconds || 0);
  }
  return null;
};

/**
 * Build query constraints from filters
 */
const buildQueryConstraints = (filters = {}) => {
  const constraints = [];
  const dateFilters = filters.dateFilters || {};

  // Equality filters (order must match Firestore index: type, noteTypeId, patient_id, campus_id, physiotherapist_id)
  if (filters.type && filters.type.trim() !== "") {
    const typeValue = filters.type.trim();
    constraints.push(where("type", "==", typeValue));
    console.log("[Firebase] Adding type filter:", typeValue);
  }

  if (filters.noteTypeId && filters.noteTypeId.trim() !== "") {
    constraints.push(where("noteTypeId", "==", filters.noteTypeId.trim()));
  }

  if (filters.patient_id !== null && filters.patient_id !== undefined) {
    constraints.push(where("patient_id", "==", filters.patient_id));
  }

  if (filters.campus_id !== null && filters.campus_id !== undefined) {
    constraints.push(where("campus_id", "==", filters.campus_id));
  }

  if (filters.physiotherapist_id !== null && filters.physiotherapist_id !== undefined) {
    constraints.push(where("physiotherapist_id", "==", filters.physiotherapist_id));
  }

  // Date range filters - only ONE range allowed in Firestore
  let orderField = "created";

  const createdFilter = dateFilters.created;
  const updatedFilter = dateFilters.updated;

  if (createdFilter?.start && createdFilter?.end) {
    const startTs = toFirestoreTimestamp(createdFilter.start);
    const endTs = toFirestoreTimestamp(createdFilter.end);

    if (startTs && endTs) {
      constraints.push(where("created", ">=", startTs));
      constraints.push(where("created", "<=", endTs));
      orderField = "created";
    }
  } else if (updatedFilter?.start && updatedFilter?.end) {
    const startTs = toFirestoreTimestamp(updatedFilter.start);
    const endTs = toFirestoreTimestamp(updatedFilter.end);

    if (startTs && endTs) {
      constraints.push(where("updated", ">=", startTs));
      constraints.push(where("updated", "<=", endTs));
      orderField = "updated";
    }
  }

  return { constraints, orderField };
};

/**
 * Fetch generated notes with proper Firestore pagination
 *
 * @param {Object} filters - Filter options
 * @param {Object} pagination - Pagination options
 * @param {number} pagination.pageSize - Items per page (default: 10)
 * @param {DocumentSnapshot} pagination.startAfterDoc - Cursor for pagination
 *
 * @returns {Object} { notes, totalCount, lastVisible, hasMore }
 */
export const fetchGeneratedNotes = async (filters = {}, pagination = {}) => {
  const { pageSize = 10, startAfterDoc = null } = pagination;

  try {
    const notesCollection = collection(db, "generatedNote");
    const { constraints, orderField } = buildQueryConstraints(filters);

    // Log the query being built for debugging
    console.log("[Firebase] Query filters:", {
      type: filters.type,
      noteTypeId: filters.noteTypeId,
      patient_id: filters.patient_id,
      campus_id: filters.campus_id,
      physiotherapist_id: filters.physiotherapist_id,
      dateFilters: filters.dateFilters,
      orderField,
      constraintCount: constraints.length,
    });

    // Count query (without pagination)
    const countQuery = query(notesCollection, ...constraints);

    // Paginated query
    const paginatedConstraints = [
      ...constraints,
      orderBy(orderField, "desc"),
    ];

    if (startAfterDoc) {
      paginatedConstraints.push(startAfter(startAfterDoc));
    }

    paginatedConstraints.push(limit(pageSize));

    const paginatedQuery = query(notesCollection, ...paginatedConstraints);

    // Execute both queries in parallel
    const [querySnapshot, countSnapshot] = await Promise.all([
      getDocs(paginatedQuery),
      getCountFromServer(countQuery),
    ]);

    const totalCount = countSnapshot.data().count;

    // Map documents to notes
    const notes = querySnapshot.docs.map((docSnapshot) => ({
      id: docSnapshot.id,
      ...docSnapshot.data(),
    }));

    // Get last document for next page cursor
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    // Check if more results exist
    const hasMore = querySnapshot.docs.length === pageSize;

    console.log(`[Firebase] Fetched ${notes.length} notes, total: ${totalCount}`);

    return {
      notes,
      totalCount,
      lastVisible,
      hasMore,
    };
  } catch (error) {
    console.error("Error fetching generated notes:", error);
    
    // Enhanced error logging
    if (error.code === "failed-precondition") {
      console.error("[Firebase] Missing index error! Check the error message for the index creation link.");
      console.error("Error details:", {
        code: error.code,
        message: error.message,
      });
      
      // Check if error message contains index link
      if (error.message && error.message.includes("https://console.firebase.google.com")) {
        console.error("⚠️  Click the link in the error message to create the missing index!");
      }
    }
    
    // Re-throw with more context
    const enhancedError = new Error(
      error.message || "Failed to fetch notes. Check console for details."
    );
    enhancedError.code = error.code;
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

/**
 * Fetch uploaded documents for a lead from crmLeads/{leadId}/documents
 *
 * @param {string|number} leadId - CRM lead ID (from assessment.lead_id)
 * @returns {Promise<Array>} Array of document objects with id, name, url, etc.
 */
export const fetchUploadedDocuments = async (leadId) => {
  if (!leadId) return [];

  try {
    // Resolve lead_id if it's array format [id, name]
    const resolvedLeadId = Array.isArray(leadId) ? leadId[0] : leadId;
    if (!resolvedLeadId) return [];

    // No orderBy: Firestore drops docs missing the ordered field, so sort client-side
    const collRef = collection(db, `crmLeads/${resolvedLeadId}/documents`);
    const snapshot = await getDocs(collRef);

    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value === "object" && "seconds" in value) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Fetch all documents for the patient of a note: documents stored on every
 * generatedNote of that patient plus uploads under crmLeads/{leadId}/documents.
 * Deduplicated by url, newest first.
 *
 * @param {Object} note - generatedNote (uses documents, patient_id, lead_id)
 * @returns {Promise<Array>} Array of document objects
 */
export const fetchPatientDocuments = async (note) => {
  if (!note) return [];

  const ownDocs = Array.isArray(note.documents) ? note.documents : [];

  const fetchNoteDocs = async () => {
    if (note.patient_id === null || note.patient_id === undefined) return [];
    try {
      const snapshot = await getDocs(
        query(collection(db, "generatedNote"), where("patient_id", "==", note.patient_id))
      );
      return snapshot.docs.flatMap((d) => {
        const docs = d.data().documents;
        return Array.isArray(docs) ? docs : [];
      });
    } catch (error) {
      console.error("Error fetching patient note documents:", error);
      return [];
    }
  };

  const leadId = note.lead_id ?? note.leadId;
  const [noteDocs, leadDocs] = await Promise.all([
    fetchNoteDocs(),
    fetchUploadedDocuments(leadId),
  ]);

  const seen = new Set();
  return [...ownDocs, ...noteDocs, ...leadDocs]
    .filter((d) => {
      if (!d || !d.url) return false;
      if (seen.has(d.url)) return false;
      seen.add(d.url);
      return true;
    })
    .sort(
      (a, b) =>
        toMillis(b.createdAt || b.uploadedAt || b.created) -
        toMillis(a.createdAt || a.uploadedAt || a.created)
    );
};

export { db, auth };